import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { Loading } from '../../../components';
import { MASection } from '../MasterAdminSettings';

import './ghost-activity.scss';

/**
 * Ghost activity — who opened which store as its owner, and what they changed.
 *
 * Master admins only, and deliberately NOT visible to store owners (Alex,
 * 2026-10-22): a merchant seeing "Retail Circle opened your store" in their
 * own settings is a support conversation nobody asked for.
 *
 * The sessions have been recorded since the feature shipped on 2026-10-22;
 * this screen is what was missing, so the trail existed with nowhere to read
 * it.
 */

const IGhost = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3a7 7 0 0 0-7 7v10l2.5-2 2.25 2L12 18l2.25 2 2.25-2 2.5 2V10a7 7 0 0 0-7-7z" />
    <path d="M9.5 10.5v.01M14.5 10.5v.01" />
  </svg>
);

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export function GhostActivitySection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [writesOnly, setWritesOnly] = useState(false);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async (query, writes) => {
    setLoading(true);
    setError('');
    try {
      const { data: payload } = await axios.get('/api/admin/ghost/sessions', {
        params: { ...(query ? { q: query } : {}), ...(writes ? { writes_only: 1 } : {}) },
      });
      setData(payload);
    } catch (e) {
      // 404 = the feature is off for this environment, which is not an error.
      if (e?.response?.status === 404) setData({ items: [], disabled: true });
      else setError(e?.response?.data?.message || 'Could not load ghost activity.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(q.trim(), writesOnly), 300);
    return () => clearTimeout(timer);
  }, [q, writesOnly, load]);

  const openSession = async (session) => {
    if (open === session.id) { setOpen(null); setDetail(null); return; }
    setOpen(session.id);
    setDetail(null);
    try {
      const { data: payload } = await axios.get(`/api/admin/ghost/sessions/${session.id}`);
      setDetail(payload);
    } catch (e) {
      setDetail({ actions: [], error: 'Could not load what happened in this session.' });
    }
  };

  const items = data?.items || [];

  return (
    <MASection
      title="Ghost Activity"
      icon={<IGhost />}
      headerExtra={data ? <span className="ma-section__count">{items.length} in {data.days || 30} days</span> : null}
    >
      <p className="rc-ghosta__intro">
        Every time a master admin opened a store as its owner. Visible here only —
        store owners never see it.
      </p>

      <div className="rc-ghosta__tools">
        <label className="rc-ghosta__search">
          <span className="rc-ghosta__vh">Search by store or master admin</span>
          <input
            type="search"
            value={q}
            placeholder="Store or master admin"
            onChange={(e) => setQ(e.target.value)}
          />
        </label>

        <button
          type="button"
          aria-pressed={writesOnly}
          className={`rc-ghosta__toggle ${writesOnly ? 'rc-ghosta__toggle--on' : ''}`}
          onClick={() => setWritesOnly((v) => !v)}
        >
          Read + write only
        </button>
      </div>

      {error && <p className="rc-ghosta__error" role="alert">{error}</p>}

      {loading && !data ? <Loading /> : (
        <div className="rc-ghosta__list">
          {data?.disabled && (
            <p className="rc-ghosta__empty">Ghost view is off for this environment.</p>
          )}

          {!data?.disabled && items.length === 0 && (
            <p className="rc-ghosta__empty">No ghost sessions in this window.</p>
          )}

          {items.map((s) => (
            <div className="rc-ghosta__item" key={s.id}>
              <button
                type="button"
                className="rc-ghosta__row"
                aria-expanded={open === s.id}
                onClick={() => openSession(s)}
              >
                <span className="rc-ghosta__who">
                  <span className="rc-ghosta__store">{s.store || `Store ${s.store_id}`}</span>
                  <span className="rc-ghosta__meta">
                    {s.master} as {s.acting_as} · {s.entry_method === 'password' ? 'platform password' : 'owner code'}
                  </span>
                </span>

                <span className={`rc-ghosta__mode rc-ghosta__mode--${s.mode}`}>
                  {s.mode === 'write' ? 'Read + write' : 'Read-only'}
                </span>

                <span className="rc-ghosta__changes">
                  {s.mode === 'write' ? `${s.changes} change${s.changes === 1 ? '' : 's'}` : '—'}
                </span>

                <span className="rc-ghosta__when">
                  {when(s.started_at)}
                  {s.active && <span className="rc-ghosta__live">live now</span>}
                </span>
              </button>

              {open === s.id && (
                <div className="rc-ghosta__detail">
                  {!detail && <p className="rc-ghosta__empty">Loading…</p>}
                  {detail?.error && <p className="rc-ghosta__error">{detail.error}</p>}
                  {detail && !detail.error && (detail.actions || []).length === 0 && (
                    <p className="rc-ghosta__empty">Nothing was changed in this session.</p>
                  )}
                  {(detail?.actions || []).map((a) => (
                    <div className="rc-ghosta__action" key={a.id}>
                      <span className="rc-ghosta__verb">{a.method}</span>
                      <span className="rc-ghosta__summary">{a.summary || a.action || a.path}</span>
                      <span className="rc-ghosta__status">{a.status}</span>
                      <span className="rc-ghosta__when">{when(a.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </MASection>
  );
}

export default GhostActivitySection;
